import { D } from '@mobily/ts-belt';
import { Box } from '@mui/material';
import { ReferenceManyField, TextField, TopToolbar, useGetRecordId } from 'react-admin';

import {
  getSavingsFundsRequestEmployeesSortColumns,
  SavingsFundsRequestEmployee,
} from '@Common/types/bo/b2b/savings_funds_requests';

import { DatagridTyped, Pagination, ServerExportButton } from '@sh4res/bo-common/components';
import { SelectColumnsButton } from '@sh4res/bo-common/components/SelectColumnsButton';
import { CurrencyField, IdField } from '@sh4res/bo-common/fields';
import { ResourceBasePath, useSelectedColumns } from '@sh4res/bo-common/hooks';
import type { ColumnList } from '@sh4res/bo-common/utils';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

type EmployeeItem = Omit<SavingsFundsRequestEmployee, 'currency'>;
// No id column: it is the employee id, shown as such
type EmployeeColumn = Exclude<keyof EmployeeItem, 'id'>;

const testId = 'BoExternalB2bSavingsFundRequestsEmployeesList';
const labelPrefix = 'resources.bo_common.b2b.savings_funds_requests.employees.fields';
const preference = 'b2b_savings_funds_request_employees.list.columns';

const defaultColumns: EmployeeColumn[] = [
  ...(isBoExternal() ? [] : ['employee_id' as const]),
  'firstname',
  'lastname',
  'identifier',
  'registration_number',
  'social_status',
  'gross_amount',
  'contribution_amount',
  'csg_tax_amount',
  'crds_tax_amount',
  'net_amount',
];

export const SavingsFundRequestEmployeesList = ({
  resourceBasePath,
}: {
  resourceBasePath: ResourceBasePath;
}) => {
  const recordId = useGetRecordId() as number;
  const displayedColumns = useSelectedColumns<EmployeeItem>({
    preferences: preference,
    defaultColumns,
  });

  const allColumns: ColumnList<Pick<EmployeeItem, EmployeeColumn>> = {
    employee_id: (
      <IdField
        reference={`${resourceBasePath}employees`}
        label={`${labelPrefix}.employee_id`}
        data-testid={`${testId}_EmployeeIdField`}
      />
    ),
    firstname: (
      <TextField
        source="firstname"
        label={`${labelPrefix}.firstname`}
        data-testid={`${testId}_FirstnameField`}
      />
    ),
    lastname: (
      <TextField
        source="lastname"
        label={`${labelPrefix}.lastname`}
        data-testid={`${testId}_LastnameField`}
      />
    ),
    identifier: (
      <TextField
        source="identifier"
        label={`${labelPrefix}.identifier`}
        data-testid={`${testId}_IdentifierField`}
      />
    ),
    registration_number: (
      <TextField
        source="registration_number"
        label={`${labelPrefix}.registration_number`}
        data-testid={`${testId}_RegistrationNumberField`}
      />
    ),
    social_status: (
      <TextField
        source="social_status"
        label={`${labelPrefix}.social_status`}
        data-testid={`${testId}_SocialStatusField`}
      />
    ),
    gross_amount: (
      <CurrencyField
        currency="currency"
        label={`${labelPrefix}.gross_amount`}
        testId={`${testId}_GrossAmountField`}
      />
    ),
    contribution_amount: (
      <CurrencyField
        currency="currency"
        label={`${labelPrefix}.contribution_amount`}
        testId={`${testId}_ContributionAmountField`}
      />
    ),
    csg_tax_amount: (
      <CurrencyField
        currency="currency"
        label={`${labelPrefix}.csg_tax_amount`}
        testId={`${testId}_CsgTaxAmountField`}
      />
    ),
    crds_tax_amount: (
      <CurrencyField
        currency="currency"
        label={`${labelPrefix}.crds_tax_amount`}
        testId={`${testId}_CrdsTaxAmountField`}
      />
    ),
    net_amount: (
      <CurrencyField
        currency="currency"
        label={`${labelPrefix}.net_amount`}
        testId={`${testId}_NetAmountField`}
      />
    ),
  };
  // External users can't open employees, so they can't pick their id either
  const columnRegistry = isBoExternal() ? D.deleteKey(allColumns, 'employee_id') : allColumns;

  return (
    <ReferenceManyField
      data-testid={`${testId}_EmployeesList`}
      reference={`${resourceBasePath}savings_funds_requests/employees`}
      target="savings_funds_request_id"
      perPage={10}
      pagination={<Pagination />}
      sort={{ field: 'employee_id', order: 'ASC' }}>
      <Box sx={{ padding: '0 15px' }}>
        <TopToolbar>
          <SelectColumnsButton<Pick<EmployeeItem, EmployeeColumn>>
            preference={preference}
            columns={columnRegistry as ColumnList<Pick<EmployeeItem, EmployeeColumn>>}
            defaultColumns={defaultColumns}
            testId={`${testId}_SelectColumnsButton`}
          />
          {/* One row per order, as the campaign exports one row per allocation choice */}
          <ServerExportButton
            resource={`${resourceBasePath}orders`}
            label="resources.bo_common.b2b.savings_funds_requests.orders.export.button"
            getBody={() => ({
              kind: 'savings_funds_request_orders',
              savings_funds_request_id: recordId,
            })}
            data-testid={`${testId}_ExportButton`}
          />
        </TopToolbar>
      </Box>
      <DatagridTyped<EmployeeItem>
        rowClick={false}
        sortableColumns={getSavingsFundsRequestEmployeesSortColumns}
        columnRegistry={columnRegistry as ColumnList<EmployeeItem>}
        displayedColumns={displayedColumns}
        bulkActionButtons={false}
        showCard={true}
      />
    </ReferenceManyField>
  );
};
